import {
  collection, doc, getDoc, getDocs, query, where, limit,
} from "firebase/firestore";
import { db } from "../firebase";
import { getCreatorTiers } from "./supportTierService";
import { dropGross, getDropStatus } from "../pages/creator/creatorHelpers";

export async function fetchCreatorDrops(creatorId) {
  if (!creatorId) return [];
  const snap = await getDocs(query(collection(db, "drops"), where("creatorId", "==", creatorId)));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
}

export async function fetchEarningsDoc(creatorId) {
  if (!creatorId) return null;
  const snap = await getDoc(doc(db, "earnings", creatorId));
  return snap.exists() ? snap.data() : null;
}

export async function fetchSupporters(creatorId) {
  if (!creatorId) return [];
  const snap = await getDocs(query(collection(db, "supporters"), where("creatorId", "==", creatorId)));
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const enriched = await Promise.all(
    rows.map(async (row) => {
      let fan = null;
      if (row.userId) {
        const userSnap = await getDoc(doc(db, "users", row.userId)).catch(() => null);
        if (userSnap?.exists()) fan = { uid: userSnap.id, ...userSnap.data() };
      }
      return { ...row, fan };
    })
  );

  return enriched.sort((a, b) => (b.joinedAt?.seconds || 0) - (a.joinedAt?.seconds || 0));
}

export async function fetchFirstViewsForCreator(creatorId, dropIds) {
  if (!creatorId || !dropIds?.length) return [];
  const results = [];
  for (const dropId of dropIds.slice(0, 20)) {
    const snap = await getDocs(
      query(collection(db, "first_views"), where("dropId", "==", dropId), limit(100))
    ).catch(() => ({ docs: [] }));
    snap.docs?.forEach((d) => results.push({ id: d.id, ...d.data() }));
  }
  return results;
}

export async function fetchReferralsForCreator(creatorId) {
  if (!creatorId) return [];
  const snap = await getDocs(
    query(collection(db, "referrals"), where("referrerId", "==", creatorId))
  ).catch(() => ({ docs: [] }));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function loadCreatorStudioData(creatorId) {
  const [drops, tiers, earnings, supporters] = await Promise.all([
    fetchCreatorDrops(creatorId),
    getCreatorTiers(creatorId),
    fetchEarningsDoc(creatorId),
    fetchSupporters(creatorId),
  ]);

  const openDrops = drops.filter((d) => getDropStatus(d) === "open");
  const liveViews = openDrops.reduce((sum, d) => sum + Number(d.viewCount || 0), 0);

  return {
    drops,
    tiers,
    earnings,
    supporters,
    stats: {
      totalDrops: drops.length,
      openCount: openDrops.length,
      draftCount: drops.filter((d) => getDropStatus(d) === "draft").length,
      closedCount: drops.filter((d) => getDropStatus(d) === "closed").length,
      liveViews,
      subscriberCount: supporters.length,
    },
  };
}

export function buildTransactionHistory(drops, userProfile) {
  return drops
    .filter((d) => d.status !== "draft")
    .map((drop) => {
      const gross = dropGross(drop);
      const unlocks = Number(drop.viewCount || 0);
      return {
        id: drop.id,
        title: drop.title || "Untitled drop",
        unlocks,
        price: drop.price || 0,
        isFree: drop.isFree,
        gross,
        net: drop.isFree ? 0 : gross * (1 - (userProfile?.feeRate ?? 0.1)) * 0.971 - 0.3,
        date: drop.createdAt,
      };
    })
    .sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0));
}

export function computeAnalyticsFromDrops(drops) {
  const byType = {};
  const byPrice = {};
  let totalViews = 0;
  let totalSaves = 0;

  drops.forEach((drop) => {
    const type = drop.contentType || "video";
    byType[type] = byType[type] || { count: 0, views: 0, earned: dropGross(drop) };
    byType[type].count += 1;
    byType[type].views += Number(drop.viewCount || 0);

    if (!drop.isFree && drop.price) {
      const bucket = `$${Math.round(drop.price)}`;
      byPrice[bucket] = (byPrice[bucket] || 0) + 1;
    }

    totalViews += Number(drop.viewCount || 0);
    totalSaves += Number(drop.savesCount || 0);
  });

  const contentTypes = Object.entries(byType).map(([type, data]) => ({
    type,
    ...data,
    avgViews: data.count ? Math.round(data.views / data.count) : 0,
  }));

  const pricePoints = Object.entries(byPrice)
    .map(([price, count]) => ({ price, count }))
    .sort((a, b) => parseFloat(a.price.slice(1)) - parseFloat(b.price.slice(1)));

  const performance = drops
    .filter((d) => d.status !== "draft")
    .map((drop) => ({
      id: drop.id,
      title: drop.title,
      thumbnailUrl: drop.thumbnailUrl,
      views: Number(drop.viewCount || 0),
      contentType: drop.contentType,
      createdAt: drop.createdAt,
      fillRate: Math.round((Number(drop.viewCount || 0) / 100) * 100),
    }))
    .sort((a, b) => b.views - a.views);

  return {
    contentTypes,
    pricePoints,
    performance,
    totalViews,
    saveRate: totalViews ? Math.round((totalSaves / totalViews) * 100) : 0,
    avgViewsPerDrop: drops.length ? Math.round(totalViews / drops.length) : 0,
  };
}

export async function fetchFollowsForCreator(creatorId) {
  if (!creatorId) return [];
  const snap = await getDocs(query(collection(db, "follows"), where("creatorId", "==", creatorId)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fetchFirstViewsByCreator(creatorId) {
  if (!creatorId) return [];
  try {
    const snap = await getDocs(
      query(collection(db, "first_views"), where("creatorId", "==", creatorId))
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch {
    return [];
  }
}

function msToDays(ms) {
  return ms / (1000 * 60 * 60 * 24);
}

export function computeAudienceBehaviour({ follows = [], firstViews = [], drops = [] }) {
  const now = Date.now();
  const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;

  const viewerDropCounts = {};
  firstViews.forEach((fv) => {
    const uid = fv.userId;
    const dropId = fv.momentId || fv.dropId || fv.id?.split("_")?.[0];
    if (!uid) return;
    if (!viewerDropCounts[uid]) viewerDropCounts[uid] = new Set();
    if (dropId) viewerDropCounts[uid].add(dropId);
  });

  const viewerIds = Object.keys(viewerDropCounts);
  const returningCount = viewerIds.filter((uid) => viewerDropCounts[uid].size > 1).length;
  const newCount = viewerIds.length - returningCount;
  const totalViewers = viewerIds.length || 1;
  const newPct = Math.round((newCount / totalViewers) * 100);
  const returningPct = Math.round((returningCount / totalViewers) * 100);

  const unlockDelays = [];
  follows.forEach((follow) => {
    const followDate = follow.createdAt?.toDate?.();
    if (!followDate) return;
    const userViews = firstViews.filter((fv) => fv.userId === follow.followerId);
    if (!userViews.length) return;
    const earliest = userViews.reduce((min, fv) => {
      const t = fv.viewedAt?.toDate?.() || fv.createdAt?.toDate?.();
      return t && (!min || t < min) ? t : min;
    }, null);
    if (earliest) unlockDelays.push(msToDays(earliest - followDate));
  });
  const avgUnlockDays = unlockDelays.length
    ? unlockDelays.reduce((a, b) => a + b, 0) / unlockDelays.length
    : null;

  const longTermFollows = follows.filter((f) => {
    const d = f.createdAt?.toDate?.();
    return d && now - d.getTime() >= thirtyDaysMs;
  });
  const longTermActivePct = longTermFollows.length
    ? Math.round((longTermFollows.length / follows.length) * 100)
    : null;

  const repeatFirst100Pct = viewerIds.length
    ? Math.round((returningCount / viewerIds.length) * 100)
    : null;

  const publishedDrops = drops.filter((d) => d.status !== "draft");
  const freeDrops = publishedDrops.filter((d) => d.isFree);
  const churnDrop = freeDrops.sort((a, b) => (a.viewCount || 0) - (b.viewCount || 0))[0];

  const churnRate = follows.length > 0 && userProfileChurnEstimate(follows)
    ? userProfileChurnEstimate(follows)
    : null;

  return {
    newPct,
    returningPct,
    avgUnlockDays,
    churnRate,
    churnDropLabel: churnDrop
      ? `${churnDrop.title || "Untitled drop"}${churnDrop.isFree ? " (free drop)" : ""}`
      : null,
    longTermActivePct,
    repeatFirst100Pct,
    hasData: follows.length > 0 || firstViews.length > 0,
  };
}

function userProfileChurnEstimate(follows) {
  const monthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const recent = follows.filter((f) => {
    const d = f.createdAt?.toDate?.();
    return d && d.getTime() >= monthAgo;
  });
  if (follows.length < 5) return null;
  const gained = recent.length;
  const estimatedChurn = Math.max(0, Math.round(((follows.length - gained) / follows.length) * 3.2 * 10) / 10);
  return Math.min(estimatedChurn, 15);
}
